/**
 * useSpeechToText - Delegates transcription lifecycle to GenerationContext so
 * that progress and results survive tab switches and page reloads.
 *
 * Architecture (post-fix):
 *   1. Upload the file via XHR (reports 0-50% upload progress locally).
 *   2. The backend immediately returns a real job_id (async endpoint).
 *   3. Call startJob(realJobId) so GenerationContext drives polling via
 *      sttPollAdapter — progress, stage and message flow through activeJob.
 *   4. The UI reads activeJob.progress / activeJob.stage / activeJob.message
 *      for live per-stage feedback.
 *   5. On completion, resultJob.result holds the SttJobResult.
 */

import { useCallback, useState } from 'react';
import { firebaseClient } from '../services/firebase/firebase-client';
import { VideoGenerationAPI } from '../services/api';
import type { AIEnhancementOptions, TranscriptionSegment } from '../services/transcription';
import { useGeneration } from '../contexts/GenerationContext.context';
import { selectJobError } from './useGenerationJob';
import type { SttJobResult } from '../services/polling/sttPollAdapter';

export interface SpeechToTextResult {
  segments: TranscriptionSegment[];
  fullText: string;
  originalText?: string;
  detectedLanguage: string;
  processingTime: number;
  aiEnhanced?: boolean;
  refinementApplied?: boolean;
  modelUsed?: string;
}

export interface UseSpeechToTextReturn {
  isTranscribing: boolean;
  /** 0-50: XHR upload progress. 50-100: driven by backend job progress (via activeJob). */
  uploadProgress: number;
  /** Human-readable stage label from the backend (e.g. "Transcribing audio..."). */
  stageMessage: string | null;
  result: SpeechToTextResult | null;
  error: string | null;
  startTranscription: (file: File, aiEnhancement?: AIEnhancementOptions) => Promise<void>;
  reset: () => void;
}

/** Upload a file form and return the backend job_id (throws on failure). */
async function uploadForJobId(
  file: File,
  aiEnhancement: AIEnhancementOptions | undefined,
  onUploadProgress: (pct: number) => void
): Promise<string> {
  const baseUrl = await VideoGenerationAPI.getBaseUrl();
  const formData = new FormData();
  formData.append('file', file);

  if (aiEnhancement?.enabled) {
    formData.append('ai_enhanced', 'true');
    if (aiEnhancement.llmProvider) formData.append('llm_provider', aiEnhancement.llmProvider);
    if (aiEnhancement.llmModel) formData.append('llm_model', aiEnhancement.llmModel);
  }

  // Both endpoints now return a job_id immediately (async backend).
  const endpoint = aiEnhancement?.enabled
    ? `${baseUrl}/api/v1/transcription/upload-and-refine`
    : `${baseUrl}/api/v1/transcription/upload-async`;

  const authToken = await firebaseClient.getIdToken();

  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        // Upload phase maps to 0-50%
        const pct = Math.round((event.loaded / event.total) * 50);
        onUploadProgress(pct);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const res = JSON.parse(xhr.responseText) as {
            success: boolean;
            data?: { job_id?: string };
            error?: string;
          };
          if (res.success && res.data?.job_id) {
            resolve(res.data.job_id);
          } else {
            reject(new Error(res.error ?? 'Upload succeeded but no job_id returned'));
          }
        } catch {
          reject(new Error('Failed to parse upload response'));
        }
      } else {
        try {
          const errRes = JSON.parse(xhr.responseText) as { error?: string };
          reject(new Error(errRes.error ?? `Upload failed with status ${xhr.status}`));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('POST', endpoint);
    if (authToken) xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    xhr.send(formData);
  });
}

export function useSpeechToText(): UseSpeechToTextReturn {
  const { startJob, failJob, clearJob, clearResults, getLatestActiveByTab, getLatestResultByTab } =
    useGeneration();

  // XHR upload progress (0-50) is local state — it happens before the job_id
  // exists, so GenerationContext cannot own it.
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [startError, setStartError] = useState<string | null>(null);

  const activeJob = getLatestActiveByTab('stt');
  const resultJob = getLatestResultByTab('stt');

  const isTranscribing =
    !!activeJob && (activeJob.status === 'queued' || activeJob.status === 'running');

  // Merge XHR upload progress (0-50) with backend job progress (50-100).
  // While a job is active the backend progress takes precedence; during the
  // XHR phase (before job_id is known) use local uploadProgress.
  const mergedProgress: number = (() => {
    if (!isTranscribing) return uploadProgress;
    const jobProgress = activeJob?.progress ?? 0;
    // Backend emits 0-100; map to 50-100 client-side to align with upload phase.
    const backendMapped = 50 + Math.round(jobProgress / 2);
    // After upload (uploadProgress >= 50) show backend-mapped progress.
    return uploadProgress >= 50 ? Math.max(50, backendMapped) : uploadProgress;
  })();

  const stageMessage: string | null = isTranscribing ? (activeJob?.message ?? null) : null;

  const completedResult =
    resultJob?.status === 'completed' ? (resultJob.result as SttJobResult | undefined) : undefined;

  const result: SpeechToTextResult | null = completedResult
    ? {
        segments: completedResult.segments,
        fullText: completedResult.fullText,
        originalText: completedResult.originalText,
        detectedLanguage: completedResult.detectedLanguage,
        processingTime: completedResult.processingTimeSeconds,
        aiEnhanced: completedResult.aiEnhanced,
        refinementApplied: completedResult.refinementApplied,
        modelUsed: completedResult.modelUsed,
      }
    : null;

  const error = startError ?? selectJobError(activeJob, resultJob, isTranscribing)?.message ?? null;

  const startTranscription = useCallback(
    async (file: File, aiEnhancement?: AIEnhancementOptions): Promise<void> => {
      setStartError(null);
      setUploadProgress(0);

      let jobId: string;
      try {
        jobId = await uploadForJobId(file, aiEnhancement, (pct) => setUploadProgress(pct));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setStartError(message);
        return;
      }

      // Upload complete — signal 50% before handing off to GenerationContext.
      setUploadProgress(50);

      // Register the real backend job_id with GenerationContext.
      // startJob() immediately starts the sttPollAdapter interval which drives
      // progress/stage/message updates through activeJob.
      startJob({
        id: jobId,
        tabMode: 'stt',
        serverType: 'generation',
        title: 'Transcription',
      });
    },
    [startJob]
  );

  const reset = useCallback((): void => {
    setStartError(null);
    setUploadProgress(0);
    if (activeJob) {
      failJob(activeJob.id, { message: 'Cancelled by user', code: 'CANCELLED' }, 'cancelled');
      clearJob(activeJob.id);
    }
    // Every finished job of the tab, not just the latest: results stack per tab,
    // so clearing only the newest put the one before it back on screen -- a
    // failed run resurfaced as 'Generation Failed' after a successful one.
    clearResults('stt');
  }, [activeJob, failJob, clearJob, clearResults]);

  return {
    isTranscribing,
    uploadProgress: mergedProgress,
    stageMessage,
    result,
    error,
    startTranscription,
    reset,
  };
}
